import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaGraduationCap, FaUserTie } from 'react-icons/fa';
import axios from 'axios';
import './SuccessStories.css';

const SuccessStories = () => {
  const mainColor = '#006186';
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/success-stories');
      
      console.log('Загруженные истории:', response.data);
      
      if (response.data && response.data.length > 0) {
        setStories(response.data);
      } else {
        setStories([]);
      }
      
      setError(null);
    } catch (err) {
      console.error('Ошибка загрузки историй:', err);
      setError('Не удалось загрузить истории выпускников');
      setStories([]);
    } finally {
      setLoading(false);
    }
  };

  // Функции для получения данных (без _ru и _en, так как в БД только русские поля)
  const getName = (story) => story.name || 'Без имени';
  const getRole = (story) => story.role || 'Должность не указана';
  const getDescription = (story) => story.description || 'Описание отсутствует';
  const getGraduation = (story) => story.graduation || 'Выпускник ОшТУ';
  
  const getImage = (story) => {
    if (story.image_url) {
      return story.image_url;
    }
    return null;
  };

  // Категории для отображения
  const getCategoryName = (category) => {
    const categories = {
      'government': 'Государственная служба',
      'business': 'Бизнес',
      'education': 'Образование',
      'science': 'Наука',
      'culture': 'Культура',
      'sport': 'Спорт',
      'it': 'IT технологии',
      'other': 'Другое'
    };
    return categories[category] || category || 'Выпускник';
  };

  if (loading) {
    return (
      <div className="alumni-page bg-white">
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Загрузка историй выпускников...</p>
        </Container>
      </div>
    );
  }

  return (
    <div className="alumni-page bg-white">
      <section className="news-hero py-5 border-bottom">
        <Container>
          <h1 className="display-4 fw-bold mb-3" style={{ color: mainColor }}>
            Гордость ОшТУ
          </h1>
          <div className="header-line mb-4" style={{ backgroundColor: mainColor, width: '80px', height: '5px' }}></div>
          <p className="lead text-muted mb-0" style={{ maxWidth: '850px' }}>
            Выпускники нашего университета — это лидеры политики, бизнеса и науки, которые ежедневно строят будущее Кыргызстана.
          </p>
        </Container>
      </section>

      <Container className="py-5">
        {error && (
          <Alert variant="warning" className="mb-4">
            {error}
          </Alert>
        )}

        {stories.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">Истории выпускников пока не добавлены</p>
            <p className="text-muted small">
              Добавьте истории успеха в админ-панели в разделе "Истории успеха"
            </p>
          </div>
        ) : (
          <Row className="g-4">
            {stories.map((person, index) => (
              <Col xs={12} key={person.id || index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="border-0 shadow-sm alumni-full-card overflow-hidden">
                    <Row className="g-0 align-items-center">
                      <Col md={3} className="text-center bg-light d-flex align-items-center justify-content-center p-3">
                        <div className="img-holder rounded-circle overflow-hidden shadow-sm border border-4 border-white" 
                             style={{ width: '180px', height: '180px' }}>
                          {getImage(person) ? (
                            <Card.Img 
                              src={getImage(person)} 
                              alt={getName(person)}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/500x500?text=OshTU';
                              }}
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center h-100 bg-secondary bg-opacity-25">
                              <FaUserTie size={60} className="text-muted" />
                            </div>
                          )}
                        </div>
                      </Col>
                      <Col md={9}>
                        <Card.Body className="p-4">
                          <div className="d-flex align-items-center mb-2">
                            <FaUserTie className="me-2 text-muted" />
                            <h4 className="fw-bold mb-0" style={{ color: '#1a365d' }}>
                              {getName(person)}
                            </h4>
                          </div>
                          <p className="fw-bold mb-3" style={{ color: mainColor }}>
                            {getRole(person)}
                          </p>
                          <Card.Text className="text-muted mb-3 border-start ps-3 py-1">
                            {getDescription(person)}
                          </Card.Text>
                          <div className="d-flex align-items-center text-dark small fw-bold mt-auto">
                            <FaGraduationCap className="me-2" style={{ color: mainColor }} size={20} />
                            {getGraduation(person)}
                          </div>
                          
                          {person.category && (
                            <div className="mt-2">
                              <span className="badge bg-light text-dark px-3 py-2">
                                {getCategoryName(person.category)}
                              </span>
                            </div>
                          )}

                          {person.is_featured === 1 && (
                            <div className="mt-2">
                              <span className="badge" style={{ backgroundColor: mainColor, color: 'white' }}>
                                Известный выпускник
                              </span>
                            </div>
                          )}
                        </Card.Body>
                      </Col>
                    </Row>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </div>
  );
};

export default SuccessStories;